import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {CODON_LENGTH, CodonTranslationTable, NucleotideType} from 'biojs/BioConstants';
import {TranslationAttributes} from 'biojs/Translation';
import * as biojsUtil from 'biojs/Util';
import {CRISPRAnalysisSequenceAttributes, CRISPRGuide} from 'constants/CRISPRConstants';
import * as BaseEditUtil from 'molbio/crispr/BaseEditUtil';
import * as CRISPRUtil from 'molbio/crispr/CRISPRUtil';
import * as sequtil from 'molbio/SeqUtil';
import sortedBy from 'util/sortedBy';
import {ElementMouseEvent} from 'util/Types';

export interface BaseEditNewSequenceProps {
  guide: CRISPRGuide;
  sequence: CRISPRAnalysisSequenceAttributes;
  setSelection: (start: number, end: number) => void;
}

class BaseEditNewSequence extends React.Component<BaseEditNewSequenceProps> {
  getIndexInCDS = (activeTranslation: TranslationAttributes, offsetFromStart: number): number => {
    const activeTranslationOffset = this.getNumCDSBasesBeforeTranslation(activeTranslation);
    return activeTranslationOffset + offsetFromStart;
  };

  getEditedBase = (sequenceIndex: number, editScores: Array<number>): string => {
    const guideStart = CRISPRUtil.guideStart(this.props.guide, this.props.sequence.length);
    const guideIndex = sequenceIndex - guideStart;
    const uneditedBase = this.props.sequence.bases[sequenceIndex];
    if (guideIndex >= 0 && guideIndex < editScores.length && editScores[guideIndex] > 0) {
      return BaseEditUtil.getForwardStrandEditedBase(uneditedBase, this.props.guide.strand);
    }
    return uneditedBase;
  };

  // Given translation will never be cross-origin, they are not supported in base edit guides.
  getTranslationLength = (translation: TranslationAttributes): number => translation.end - translation.start;

  getIntersectingCodonsRange = (
    overlappingTranslation: TranslationAttributes,
    overlappingTranslationOffset: number,
    guideStart: number,
    guideEnd: number
  ): {codonsStart: number; codonsEnd: number} => {
    // This method returns the beginning and end of the region of the codons (coded for in
    // the overlappingTranslation) that intersect the guide specified by [guideStart, guideEnd) and adds
    // padding for codons which intersect the boundaries of this region, such that the region returned
    // contains no partial codons.
    // TODO(T5646): Refactor this into a utility function add tests.
    const effectiveTranslationStart = overlappingTranslation.start - (overlappingTranslationOffset % 3);
    let effectiveTranslationEnd =
      Math.min(guideEnd, overlappingTranslation.end) +
      sequtil.mod(effectiveTranslationStart - Math.min(guideEnd, overlappingTranslation.end), 3);
    if (effectiveTranslationEnd > overlappingTranslation.end) {
      // Verify that there are enough remaining bases in the CDS to complete the translation's last
      // overlapping codon. We assume that (effectiveTranslationEnd - overlappingTranslation.end) < 3, i.e.
      // we are adding at most a single codon to the effective translation window on the downstream side. So,
      // if we do not have enough bases remaining in this CDS, we need only shrink the window by 3.
      const sortedTranslations = this.getSortedTranslationsInGroup(overlappingTranslation);
      const overlappingTranslationIndex = sortedTranslations.indexOf(overlappingTranslation);

      const numRemainingCdsBases = sortedTranslations
        .slice(overlappingTranslationIndex + 1, sortedTranslations.length)
        .reduce((length, t) => length + this.getTranslationLength(t), 0);
      if (numRemainingCdsBases < effectiveTranslationEnd - overlappingTranslation.end) {
        effectiveTranslationEnd -= 3;
      }
    }

    let codonsStart;
    if (guideStart < effectiveTranslationStart) {
      codonsStart = effectiveTranslationStart;
    } else {
      codonsStart = guideStart - ((guideStart - effectiveTranslationStart) % 3);
    }

    let codonsEnd;
    if (guideEnd > effectiveTranslationEnd) {
      codonsEnd = effectiveTranslationEnd;
    } else if ((effectiveTranslationEnd - guideEnd) % 3 === 0) {
      codonsEnd = guideEnd;
    } else {
      codonsEnd = guideEnd + ((effectiveTranslationEnd - guideEnd) % 3);
    }

    return {codonsStart, codonsEnd};
  };

  getSortedTranslationsInGroup = (translation: TranslationAttributes): Array<TranslationAttributes> => {
    if (!translation.translationGroupId) {
      return [translation];
    }
    return sortedBy(
      this.props.sequence.translations.filter((t) => t.translationGroupId === translation.translationGroupId),
      ['start']
    );
  };

  // Only forward translations are supported for base edit guides so we do not need to account for strand
  // when determining which regions are upstream.
  getSortedUpstreamTranslationsInGroup = (translation: TranslationAttributes): Array<TranslationAttributes> =>
    this.getSortedTranslationsInGroup(translation).filter((t) => t.end <= translation.start && t.end !== 0);

  getNumCDSBasesBeforeTranslation = (translation: TranslationAttributes): number =>
    this.getSortedUpstreamTranslationsInGroup(translation).reduce(
      (runningSum, currentTranslation) => runningSum + this.getTranslationLength(currentTranslation),
      0
    );

  handleOnClickAminoAcid = (e: ElementMouseEvent, start: number, end: number): void => {
    this.props.setSelection(start, end);
    e.stopPropagation();
  };

  renderBaseInGuide = (guideIndex: number, editScores: Array<number>): JSX.Element => {
    const base = CRISPRUtil.getForwardStrandBases(this.props.guide)[guideIndex];
    if (editScores[guideIndex] > 0) {
      return (
        <span className="baseEdit-guidesTable-base-edited" key={guideIndex}>
          {BaseEditUtil.getForwardStrandEditedBase(base, this.props.guide.strand)}
        </span>
      );
    }
    return <span key={guideIndex}>{base}</span>;
  };

  renderNewSequenceNoCodons = (isUnsupported: boolean): JSX.Element => {
    const editScores = BaseEditUtil.getForwardStrandEditScores(this.props.guide, this.props.sequence);

    return (
      <div className={classNames('mono text-left', {'baseEdit-guidesTable-unsupported': isUnsupported})}>
        {_.range(this.props.guide.bases.length).map((guideIndex) =>
          this.renderBaseInGuide(guideIndex, editScores)
        )}
      </div>
    );
  };

  renderSequenceSegmentAminoAcids = (
    translation: TranslationAttributes,
    uneditedBases: string,
    start: number,
    editScores: Array<number>
  ): JSX.Element | null => {
    const translationBases = this.getSortedTranslationsInGroup(translation).reduce(
      (runningBases, t) => runningBases + this.props.sequence.bases.slice(t.start, t.end),
      ''
    );
    const uneditedBasesInCDS = uneditedBases
      .split('')
      .map((uneditedBase, i) => {
        const sequenceIndex = start + i;

        const translationIndex = this.getIndexInCDS(translation, sequenceIndex - translation.start);
        assert(
          0 <= translationIndex && translationIndex < translationBases.length,
          'Expected translation index to be within translation length'
        );
        return translationBases[translationIndex];
      })
      .join('');
    const editedBasesInCDS = uneditedBases
      .split('')
      .map((uneditedBase, i) => {
        const sequenceIndex = start + i;
        const translationIndex = this.getIndexInCDS(translation, sequenceIndex - translation.start);
        assert(
          0 <= translationIndex && translationIndex < translationBases.length,
          'Expected translation index to be within translation length'
        );
        if (sequenceIndex < translation.start || translation.end <= sequenceIndex) {
          return translationBases[translationIndex];
        }
        return this.getEditedBase(sequenceIndex, editScores);
      })
      .join('');
    const uneditedTranslation = biojsUtil.translateOnStrand(
      uneditedBasesInCDS,
      1,
      NucleotideType.DNA,
      CodonTranslationTable.STANDARD,
      false
    );
    const editedTranslation = biojsUtil.translateOnStrand(
      editedBasesInCDS,
      1,
      NucleotideType.DNA,
      CodonTranslationTable.STANDARD,
      false
    );

    return (
      <span
        className={classNames({
          'baseEdit-guidesTable-aminoAcid-edited': uneditedTranslation !== editedTranslation,
        })}
      >
        {' '}
        {editedTranslation}{' '}
      </span>
    );
  };

  renderSequenceSegment = (
    start: number,
    end: number,
    translation: TranslationAttributes | null
  ): JSX.Element | null => {
    if (start >= end) {
      return null;
    }
    const guideStart = CRISPRUtil.guideStart(this.props.guide, this.props.sequence.length);
    const guideEnd = CRISPRUtil.guideEnd(this.props.guide, this.props.sequence.length);
    const editScores = BaseEditUtil.getForwardStrandEditScores(this.props.guide, this.props.sequence);
    const uneditedBases = this.props.sequence.bases.slice(start, end);
    const editedBasesInSequence = uneditedBases
      .split('')
      .map((uneditedBase, i) => {
        const sequenceIndex = start + i;
        return this.getEditedBase(sequenceIndex, editScores);
      })
      .join('');

    return (
      <div
        className="baseEdit-guidesTable-sequenceSegment"
        key={`segment-${start}`}
        onClick={(e) => {
          this.handleOnClickAminoAcid(e, start, end);
        }}
      >
        <div key="bases">
          {editedBasesInSequence.split('').map((editedBase, i) => {
            const key = `bases-${i}`;
            const sequenceIndex = start + i;
            if (translation) {
              // There is a translation associated with this segment, so if any of the bases do not actually
              // occur in this translation (but rather a different translation in the same translation group)
              // then we should render a space in its place.
              if (sequenceIndex < translation.start || translation.end <= sequenceIndex) {
                return <span key={key}>{' ' /* prettier-ignore */}</span>;
              }
            }
            if (sequenceIndex < guideStart || guideEnd <= sequenceIndex) {
              return (
                <span key={key} className="baseEdit-guidesTable-base-notInGuide">
                  {editedBase}
                </span>
              );
            } else if (editedBase !== uneditedBases[i]) {
              return (
                <span key={key} className="baseEdit-guidesTable-base-edited">
                  {editedBase}
                </span>
              );
            }
            return <span key={key}>{editedBase}</span>;
          })}
        </div>
        <div key="aminoAcids">
          {translation != null &&
            this.renderSequenceSegmentAminoAcids(translation, uneditedBases, start, editScores)}
        </div>
      </div>
    );
  };

  renderSequenceWithTranslation = (): JSX.Element => {
    const overlappingTranslation = BaseEditUtil.getOverlappingTranslationsForGuide(
      this.props.guide,
      this.props.sequence
    )[0];
    const guideStart = CRISPRUtil.guideStart(this.props.guide, this.props.sequence.length);
    const guideEnd = CRISPRUtil.guideEnd(this.props.guide, this.props.sequence.length);

    const {codonsStart, codonsEnd} = this.getIntersectingCodonsRange(
      overlappingTranslation,
      this.getNumCDSBasesBeforeTranslation(overlappingTranslation),
      guideStart,
      guideEnd
    );

    const codonsRegions = [];
    let nextRegionStart = codonsStart;
    while (nextRegionStart < codonsEnd) {
      codonsRegions.push([nextRegionStart, nextRegionStart + CODON_LENGTH]);
      nextRegionStart += CODON_LENGTH;
    }

    const renderStart = Math.min(guideStart, codonsStart);

    return (
      <div className="mono baseEdit-guidesTable-flexContainer">
        {this.renderSequenceSegment(renderStart, overlappingTranslation.start, null)}
        {codonsRegions.map((region) => {
          const [regionStart, regionEnd] = region;
          return this.renderSequenceSegment(regionStart, regionEnd, overlappingTranslation);
        })}
        {this.renderSequenceSegment(overlappingTranslation.end, guideEnd, null)}
      </div>
    );
  };

  render(): JSX.Element {
    const isUnsupported = BaseEditUtil.hasInvalidOverlappingTranslations(
      this.props.guide,
      this.props.sequence
    );
    const overlappingTranslations = BaseEditUtil.getOverlappingTranslationsForGuide(
      this.props.guide,
      this.props.sequence
    );
    if (isUnsupported || overlappingTranslations.length === 0) {
      return this.renderNewSequenceNoCodons(isUnsupported);
    }
    return this.renderSequenceWithTranslation();
  }
}

export default BaseEditNewSequence;
